import Chart from 'chart.js/auto';
import { useRef, useEffect } from 'react';
import 'chartjs-plugin-style';
import { useIntl } from 'react-intl';
import 'chartjs-plugin-style';

import { useThrottledWindowSize } from 'src/hooks/useWindowSize';
import { breakpoints } from 'src/styles/theme/breakpoints';

import { dataSets } from './weight-loss-by-percentage-chart-data';
import tooltips from './weight-loss-by-percentage-chart-tooltips';
import messages from './weight-loss-by-percentage-chart.messages';

Chart.defaults.font.family = "'GT-America-Standard-Regular', 'PT Sans', sans-serif";

type Props = {
  id: string;
  data: Record<string, any[]>;
};

export function WeightLossByPercentageChart({ id, data }: Props) {
  const intl = useIntl();

  const canvasRef = useRef<any>(null);
  const chartRef = useRef<any>(null);

  const [width] = useThrottledWindowSize();

  const isMobile = width < breakpoints.tablet;

  useEffect(() => {
    const ctx = canvasRef.current.getContext('2d');

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    const labels = [
      intl.formatMessage({ id: messages.Month1.id }),
      intl.formatMessage({ id: messages.Month2.id }),
      intl.formatMessage({ id: messages.Month3.id }),
      intl.formatMessage({ id: messages.Month4.id }),
      intl.formatMessage({ id: messages.Month5.id }),
      intl.formatMessage({ id: messages.Month6.id }),
      intl.formatMessage({ id: messages.Month7.id }),
      intl.formatMessage({ id: messages.Month8.id }),
      intl.formatMessage({ id: messages.Month9.id }),
      intl.formatMessage({ id: messages.Month10.id }),
      intl.formatMessage({ id: messages.Month11.id }),
      intl.formatMessage({ id: messages.Month12.id }),
    ];

    // NOTE: Splice the array so we don't show empty months.
    const totalMonths: number = data.Median?.length;
    const labelsWithoutCurrentMonth = labels.splice(0, totalMonths - 1);
    const dataWithoutCurrentMonth = Object.entries(data).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: value.splice(0, totalMonths - 1),
      }),
      {}
    );

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labelsWithoutCurrentMonth,
        datasets: dataSets(dataWithoutCurrentMonth, isMobile, intl),
      },
      options: {
        // Customize chart options
        // fill: 'red',
        responsive: true,
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: isMobile ? 15 : 25,
            bottom: 0,
          },
        },
        elements: {
          line: {
            tension: 0.4,
          },
        },
        scales: {
          x: {
            title: {
              display: true,
              text: intl.formatMessage({ id: messages.monthsInto.id }),
              font: {
                family: 'GT-America-Standard-Medium',
                size: isMobile ? 6 : 13,
              },
              padding: isMobile ? 5 : 20,
            },
            ticks: {
              display: true,
              font: {
                size: isMobile ? 6 : 13,
              },
            },
            grid: {
              display: false,
            },
            border: {
              display: false,
            },
          },
          y: {
            title: {
              display: true,
              text: 'TBWL %',
              font: {
                family: 'GT-America-Standard-Medium',
                size: isMobile ? 6 : 13,
              },
              padding: isMobile ? 5 : 20,
            },
            ticks: {
              display: true,
              padding: isMobile ? 5 : 20,
              font: {
                size: isMobile ? 6 : 13,
              },
              callback: (value) => ` ${value}% `,
            },
            grid: {
              display: true,
            },
            border: {
              display: false,
            },
          },
        },
        plugins: {
          tooltip: tooltips,
          legend: {
            display: false,
          },
        },
      },
    });
  }, [isMobile, data, intl]);

  return (
    <div>
      <canvas id={id} ref={canvasRef} />
    </div>
  );
}
